/**
 * Capture Settings Options & Constraints
 *
 * Vehicle-specific option lists, formatters, and defaults for photo/video capture settings.
 * Shared between cloud web (customer portal) and mobile (flyby-rn-logic).
 *
 * @packageDocumentation
 */

import { AwbMode_Enum } from "@skydio/pbtypes/pbtypes/gen/user_camera/awb_mode";
import { CompensationMode_Enum } from "@skydio/pbtypes/pbtypes/gen/user_camera/compensation_mode";
import { ExposureMode_Enum } from "@skydio/pbtypes/pbtypes/gen/user_camera/exposure_mode";
import { IsoMode_Enum } from "@skydio/pbtypes/pbtypes/gen/user_camera/iso_mode";
import { RecordingMode_Enum } from "@skydio/pbtypes/pbtypes/gen/user_camera/recording_mode";
import { PhotoFormatSettings_PhotoFormat } from "@skydio/pbtypes/pbtypes/vehicle/skills/mission_camera_settings";

import { hasV100L, hasVT300L, hasVT300Z, isX10 } from "./constraints";

import type { VehicleConfiguration } from "./constraints";

// ============================================================================
// White Balance
// ============================================================================

const AWB_OPTIONS_OTHER = [
  AwbMode_Enum.AWB_MODE_2300K,
  AwbMode_Enum.AWB_MODE_2800K,
  AwbMode_Enum.AWB_MODE_3200K,
  AwbMode_Enum.AWB_MODE_3800K,
  AwbMode_Enum.AWB_MODE_4300K,
  AwbMode_Enum.AWB_MODE_5000K,
  AwbMode_Enum.AWB_MODE_6500K,
  AwbMode_Enum.AWB_MODE_7500K,
];

const AWB_OPTIONS_X10 = [
  AwbMode_Enum.AWB_MODE_2300K,
  AwbMode_Enum.AWB_MODE_3200K,
  AwbMode_Enum.AWB_MODE_3800K,
  AwbMode_Enum.AWB_MODE_4300K,
  AwbMode_Enum.AWB_MODE_4800K,
  AwbMode_Enum.AWB_MODE_5000K,
  AwbMode_Enum.AWB_MODE_5400K,
  AwbMode_Enum.AWB_MODE_5800K,
  AwbMode_Enum.AWB_MODE_6000K,
  AwbMode_Enum.AWB_MODE_6500K,
];

export function getWhiteBalanceOptions(vehicleConfig?: VehicleConfiguration): AwbMode_Enum[] {
  return isX10(vehicleConfig) ? AWB_OPTIONS_X10 : AWB_OPTIONS_OTHER;
}

export function formatWhiteBalance(value: AwbMode_Enum): string {
  return `${value}K`;
}

export const DEFAULT_WHITE_BALANCE = AwbMode_Enum.AWB_MODE_5000K;

// ============================================================================
// Shutter Speed
// ============================================================================

const SHUTTER_OPTIONS_OTHER = [
  ExposureMode_Enum.EXP_MODE_30,
  ExposureMode_Enum.EXP_MODE_48,
  ExposureMode_Enum.EXP_MODE_60,
  ExposureMode_Enum.EXP_MODE_96,
  ExposureMode_Enum.EXP_MODE_120,
  ExposureMode_Enum.EXP_MODE_240,
  ExposureMode_Enum.EXP_MODE_480,
  ExposureMode_Enum.EXP_MODE_960,
  ExposureMode_Enum.EXP_MODE_1920,
];

const SHUTTER_OPTIONS_X10 = [
  ExposureMode_Enum.EXP_MODE_30,
  ExposureMode_Enum.EXP_MODE_48,
  ExposureMode_Enum.EXP_MODE_60,
  ExposureMode_Enum.EXP_MODE_96,
  ExposureMode_Enum.EXP_MODE_120,
  ExposureMode_Enum.EXP_MODE_240,
  ExposureMode_Enum.EXP_MODE_480,
  ExposureMode_Enum.EXP_MODE_960,
  ExposureMode_Enum.EXP_MODE_1920,
  ExposureMode_Enum.EXP_MODE_3840,
  ExposureMode_Enum.EXP_MODE_7680,
];

export function getShutterSpeedOptions(vehicleConfig?: VehicleConfiguration): ExposureMode_Enum[] {
  return isX10(vehicleConfig) ? SHUTTER_OPTIONS_X10 : SHUTTER_OPTIONS_OTHER;
}

export function formatShutterSpeed(value: ExposureMode_Enum): string {
  return `1/${Math.round(1e9 / value)}`;
}

export const DEFAULT_SHUTTER_SPEED = ExposureMode_Enum.EXP_MODE_60;

// ============================================================================
// ISO
// ============================================================================

const ISO_OPTIONS_OTHER = [
  IsoMode_Enum.ISO_MODE_100,
  IsoMode_Enum.ISO_MODE_200,
  IsoMode_Enum.ISO_MODE_400,
  IsoMode_Enum.ISO_MODE_800,
  IsoMode_Enum.ISO_MODE_1600,
  IsoMode_Enum.ISO_MODE_3200,
  IsoMode_Enum.ISO_MODE_6400,
];

const ISO_OPTIONS_X10 = [
  IsoMode_Enum.ISO_MODE_100,
  IsoMode_Enum.ISO_MODE_200,
  IsoMode_Enum.ISO_MODE_400,
  IsoMode_Enum.ISO_MODE_800,
  IsoMode_Enum.ISO_MODE_1600,
  IsoMode_Enum.ISO_MODE_3200,
  IsoMode_Enum.ISO_MODE_6400,
  IsoMode_Enum.ISO_MODE_9600,
  IsoMode_Enum.ISO_MODE_12800,
  IsoMode_Enum.ISO_MODE_16000,
];

export function getIsoOptions(vehicleConfig?: VehicleConfiguration): IsoMode_Enum[] {
  return isX10(vehicleConfig) ? ISO_OPTIONS_X10 : ISO_OPTIONS_OTHER;
}

export function formatIso(value: IsoMode_Enum): string {
  return `${value}`;
}

export const DEFAULT_ISO = IsoMode_Enum.ISO_MODE_400;

// ============================================================================
// Brightness / Exposure Compensation (EV)
// ============================================================================

const EV_OPTIONS_OTHER = [
  CompensationMode_Enum.COMP_MODE_N20,
  CompensationMode_Enum.COMP_MODE_N15,
  CompensationMode_Enum.COMP_MODE_N10,
  CompensationMode_Enum.COMP_MODE_N05,
  CompensationMode_Enum.COMP_MODE_0,
  CompensationMode_Enum.COMP_MODE_05,
  CompensationMode_Enum.COMP_MODE_10,
  CompensationMode_Enum.COMP_MODE_15,
  CompensationMode_Enum.COMP_MODE_20,
];

const EV_OPTIONS_X10 = [
  CompensationMode_Enum.COMP_MODE_N30,
  CompensationMode_Enum.COMP_MODE_N25,
  CompensationMode_Enum.COMP_MODE_N20,
  CompensationMode_Enum.COMP_MODE_N15,
  CompensationMode_Enum.COMP_MODE_N10,
  CompensationMode_Enum.COMP_MODE_N05,
  CompensationMode_Enum.COMP_MODE_0,
  CompensationMode_Enum.COMP_MODE_05,
  CompensationMode_Enum.COMP_MODE_10,
  CompensationMode_Enum.COMP_MODE_15,
  CompensationMode_Enum.COMP_MODE_20,
  CompensationMode_Enum.COMP_MODE_25,
  CompensationMode_Enum.COMP_MODE_30,
];

export function getExposureCompensationOptions(
  vehicleConfig?: VehicleConfiguration
): CompensationMode_Enum[] {
  return isX10(vehicleConfig) ? EV_OPTIONS_X10 : EV_OPTIONS_OTHER;
}

export function formatExposureCompensation(value: CompensationMode_Enum): string {
  return (value / 10).toString();
}

export const DEFAULT_EXPOSURE_COMPENSATION = CompensationMode_Enum.COMP_MODE_0;

/**
 * EV compensation is disabled when both ISO and shutter speed are manually locked.
 * When disabled, EV should be reset to COMP_MODE_0.
 */
export function isExposureCompensationDisabled(
  isoMode: IsoMode_Enum,
  exposureMode: ExposureMode_Enum
): boolean {
  return isoMode !== IsoMode_Enum.ISO_MODE_AUTO && exposureMode !== ExposureMode_Enum.EXP_MODE_AUTO;
}

// ============================================================================
// Camera Mode (Recording Mode for photos)
// ============================================================================

export interface CameraModeOption {
  key: string;
  value: RecordingMode_Enum;
  label: string;
}

const CAMERA_MODE_OPTIONS_X10: CameraModeOption[] = [
  { key: "standard", value: RecordingMode_Enum.PHOTO_DEFAULT, label: "Standard" },
  { key: "hdr", value: RecordingMode_Enum.PHOTO_HDR, label: "HDR" },
  { key: "low_light", value: RecordingMode_Enum.PHOTO_NIGHT, label: "Low Light" },
];

const CAMERA_MODE_OPTIONS_OTHER: CameraModeOption[] = [
  { key: "standard", value: RecordingMode_Enum.PHOTO_DEFAULT, label: "Standard" },
  { key: "hdr", value: RecordingMode_Enum.PHOTO_HDR, label: "HDR" },
];

export function getCameraModeOptions(vehicleConfig?: VehicleConfiguration): CameraModeOption[] {
  return isX10(vehicleConfig) ? CAMERA_MODE_OPTIONS_X10 : CAMERA_MODE_OPTIONS_OTHER;
}

// ============================================================================
// Resolution (X10 only: Full vs 1/4)
// ============================================================================

export enum PhotoResolution {
  FULL = "FULL",
  QUARTER_BINNED = "QUARTER_BINNED",
}

export interface ResolutionOption {
  key: string;
  value: PhotoResolution;
  label: string;
}

const RESOLUTION_OPTIONS_X10: ResolutionOption[] = [
  { key: "quarter", value: PhotoResolution.QUARTER_BINNED, label: "1/4" },
  { key: "full", value: PhotoResolution.FULL, label: "Full" },
];

/**
 * Resolution options (X10 only — other vehicles don't have resolution selection).
 * Returns undefined for non-X10 vehicles.
 */
export function getResolutionOptions(
  vehicleConfig?: VehicleConfiguration
): ResolutionOption[] | undefined {
  return isX10(vehicleConfig) ? RESOLUTION_OPTIONS_X10 : undefined;
}

/**
 * Convert between RecordingMode_Enum and PhotoResolution.
 * PHOTO_HIGH_RES = Full resolution, everything else = 1/4 binned.
 */
export function recordingModeToResolution(mode?: RecordingMode_Enum): PhotoResolution {
  return mode === RecordingMode_Enum.PHOTO_HIGH_RES
    ? PhotoResolution.FULL
    : PhotoResolution.QUARTER_BINNED;
}

/**
 * When resolution changes, map back to a RecordingMode_Enum.
 * Full → PHOTO_HIGH_RES, 1/4 → PHOTO_DEFAULT.
 */
export function resolutionToRecordingMode(resolution: PhotoResolution): RecordingMode_Enum {
  return resolution === PhotoResolution.FULL
    ? RecordingMode_Enum.PHOTO_HIGH_RES
    : RecordingMode_Enum.PHOTO_DEFAULT;
}

/**
 * When resolution is Full, only Standard camera mode is available.
 * HDR and Low Light require 1/4 resolution.
 */
export function getAvailableCameraModes(
  resolution: PhotoResolution,
  vehicleConfig?: VehicleConfiguration
): CameraModeOption[] {
  const allModes = getCameraModeOptions(vehicleConfig);
  if (resolution === PhotoResolution.FULL) {
    return allModes.filter(m => m.value === RecordingMode_Enum.PHOTO_DEFAULT);
  }
  return allModes;
}

// ============================================================================
// Photo Format (File Type)
// ============================================================================

export interface PhotoFormatOption {
  key: string;
  value: PhotoFormatSettings_PhotoFormat[];
  label: string;
}

export const PHOTO_FORMAT_OPTIONS: PhotoFormatOption[] = [
  {
    key: "jpg",
    value: [PhotoFormatSettings_PhotoFormat.JPEG],
    label: "JPG",
  },
  {
    key: "dng_jpg",
    value: [PhotoFormatSettings_PhotoFormat.JPEG, PhotoFormatSettings_PhotoFormat.DNG],
    label: "DNG + JPG",
  },
];

// ============================================================================
// Thermal Format
// ============================================================================

export interface ThermalFormatOption {
  key: string;
  value: boolean;
  label: string;
}

export const THERMAL_FORMAT_OPTIONS: ThermalFormatOption[] = [
  { key: "jpg", value: false, label: "JPG" },
  { key: "rjpg", value: true, label: "JPG + RJPG" },
];

export function thermalFormatsToRjpegEnabled(formats?: PhotoFormatSettings_PhotoFormat[]): boolean {
  return formats?.includes(PhotoFormatSettings_PhotoFormat.RJPEG) ?? false;
}

export function rjpegEnabledToThermalFormats(
  rjpegEnabled: boolean
): PhotoFormatSettings_PhotoFormat[] {
  return rjpegEnabled
    ? [PhotoFormatSettings_PhotoFormat.JPEG, PhotoFormatSettings_PhotoFormat.RJPEG]
    : [PhotoFormatSettings_PhotoFormat.JPEG];
}

// ============================================================================
// Zoom
// ============================================================================

export interface ZoomConstraints {
  min: number;
  max: number;
  step: number;
}

export function getZoomConstraints(vehicleConfig?: VehicleConfiguration): ZoomConstraints {
  if (hasVT300L(vehicleConfig) || hasV100L(vehicleConfig) || hasVT300Z(vehicleConfig)) {
    return { min: 1, max: 32, step: 0.1 };
  }
  return { min: 1, max: 16, step: 0.1 };
}

export const DEFAULT_ZOOM = 1;
